import { getLinesFromChange } from 'components/Diff/helpers/getLinesFromChange';
import type { Change } from 'diff';

export type ChangedRowBlock = {
  added: string[];
  removed: string[];
};

export type UnchangedRowBlock = {
  unchanged: string[];
};

export type RowBlock = ChangedRowBlock | UnchangedRowBlock;

export const isChangedRowBlock = (
  rowBlock: RowBlock,
): rowBlock is ChangedRowBlock => 'added' in rowBlock;

export const isUnchangedRowBlock = (
  rowBlock: RowBlock,
): rowBlock is UnchangedRowBlock => 'unchanged' in rowBlock;

/**
 * Organize changes into row blocks
 *
 * Either of unchanged lines, or of added and removed lines
 */
export const changesToRowBlocks = (changes: Change[]): RowBlock[] => {
  const rowBlocks: RowBlock[] = [];

  const processChangedLines = (change: Change, lines: string[]): void => {
    let last: RowBlock | undefined = rowBlocks[rowBlocks.length - 1];
    if (!last || !isChangedRowBlock(last)) {
      last = { added: [], removed: [] };
      rowBlocks.push(last);
    }
    if (change.added) {
      last.added.push(...lines);
    } else {
      last.removed.push(...lines);
    }
  };

  const processUnchangedLines = (lines: string[]): void => {
    let last: RowBlock | undefined = rowBlocks[rowBlocks.length - 1];
    if (!last || !isUnchangedRowBlock(last)) {
      last = { unchanged: [] };
      rowBlocks.push(last);
    }
    last.unchanged.push(...lines);
  };

  for (const change of changes) {
    const lines = getLinesFromChange(change);
    if (change.added || change.removed) {
      processChangedLines(change, lines);
    } else {
      processUnchangedLines(lines);
    }
  }
  return rowBlocks;
};
